import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Briefcase,
  Clock,
  Building2,
  Home as HomeIcon,
  Sparkles,
  Download,
} from 'lucide-react';

import { StatusPill } from '@/components/ui/status-pill';
import { Badge } from '@/components/ui/badge';
import { cn, formatDuration, formatTime } from '@/lib/utils';
import { useAuth } from '@/lib/use-auth';
import { ApiError, api } from '@/lib/api';
import { OvertimeApprovalModal } from './overtime/OvertimeApprovalModal';

type PunchType = 'IN' | 'OUT';
type DeclaredLocation = 'OFFICE' | 'REMOTE';
type WorkdayIncident = 'NO_OUT' | 'OUT_WITHOUT_IN' | 'DOUBLE_IN';
type WorkdayStatus = 'OPEN' | 'CLOSED' | 'INCIDENT';

interface PunchRecord {
  id: string;
  type: PunchType;
  eventAt: string;
  declaredLocation: DeclaredLocation;
}

interface PendingOvertime {
  workdayId: string;
  minutes: number;
}

interface MySettings {
  notifyByEmail: boolean;
  overtimeCompensationEnabled: boolean;
}

interface PunchResponse {
  punch: PunchRecord;
  pendingOvertime: PendingOvertime | null;
}

interface ProjectOption {
  id: string;
  name: string;
  color: string;
  isActive: boolean;
}

interface CurrentProject {
  id: string;
  name: string;
  color: string;
}

interface TodayResponse {
  status: 'IN' | 'OUT';
  lastPunchAt: string | null;
  lastDeclaredLocation: DeclaredLocation | null;
  currentProject: CurrentProject | null;
  lastProjectId: string | null;
  workdayId: string | null;
  punchesToday: PunchRecord[];
  expectedMinutes: number;
  breakMinutes: number;
  workedMinutes: number;
  balanceMinutes: number;
  workdayStatus: WorkdayStatus;
  incidents: WorkdayIncident[];
}

interface RangeWorkday {
  id: string;
  date: string;
  expectedMinutes: number;
  workedMinutes: number;
  breakMinutes: number;
  balanceMinutes: number;
  status: WorkdayStatus;
}

interface RangeItem {
  workday: RangeWorkday;
  incidents: WorkdayIncident[];
}

interface RangeResponse {
  from: string;
  to: string;
  items: RangeItem[];
  summary: {
    totalExpectedMinutes: number;
    totalWorkedMinutes: number;
    totalBreakMinutes: number;
    totalBalanceMinutes: number;
    daysOpen: number;
    daysClosed: number;
    daysIncident: number;
  };
}

export function Punch() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const today = useQuery({
    queryKey: ['punch', 'today'],
    queryFn: () => api<TodayResponse>('/punch/today'),
    refetchOnWindowFocus: true,
  });

  const projects = useQuery({
    queryKey: ['projects', 'active'],
    queryFn: () => api<ProjectOption[]>('/projects'),
  });

  const range = useQuery({
    queryKey: ['workdays', 'range', monthStartYmd(), todayYmd()],
    queryFn: () =>
      api<RangeResponse>(`/workdays/range?from=${rangeStartYmd()}&to=${todayYmd()}`),
    refetchOnWindowFocus: true,
  });

  const status = today.data?.status ?? 'OUT';
  const lastPunchAt = useMemo(
    () => (today.data?.lastPunchAt ? new Date(today.data.lastPunchAt) : null),
    [today.data?.lastPunchAt],
  );

  const [location, setLocation] = useState<DeclaredLocation>('OFFICE');
  useEffect(() => {
    if (today.data?.lastDeclaredLocation) {
      setLocation(today.data.lastDeclaredLocation);
    }
  }, [today.data?.lastDeclaredLocation]);

  // Proyecto seleccionado para el próximo IN. null = "Sin proyecto".
  //  - Si hay un IN abierto (currentProject): siempre refleja ese proyecto.
  //  - Si no hay IN abierto y aún no hemos inicializado: usamos lastProjectId
  //    (último proyecto histórico) como semilla para no obligar al usuario a
  //    re-elegirlo cada día.
  //  - Tras la inicialización, la elección del usuario es la fuente de verdad
  //    y no se pisa por refetches.
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!today.data) return;
    if (today.data.currentProject) {
      setSelectedProjectId(today.data.currentProject.id);
      initializedRef.current = true;
      return;
    }
    if (!initializedRef.current) {
      setSelectedProjectId(today.data.lastProjectId);
      initializedRef.current = true;
    }
  }, [today.data]);

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  const elapsedMinutes = useMemo(() => {
    if (status !== 'IN' || !lastPunchAt) return 0;
    return Math.floor((now.getTime() - lastPunchAt.getTime()) / 60_000);
  }, [status, lastPunchAt, now]);

  const todayWorkedMinutes = useMemo(
    () => computeTodayWorkedMinutes(today.data?.punchesToday ?? [], now),
    [today.data?.punchesToday, now],
  );

  const expectedMinutes = today.data?.expectedMinutes ?? 0;
  const breakMinutes = today.data?.breakMinutes ?? 0;
  const balanceMinutes = todayWorkedMinutes - breakMinutes - expectedMinutes;
  const showBalance = expectedMinutes > 0;
  const incidents = today.data?.incidents ?? [];

  const { weekWorkedMinutes, monthWorkedMinutes } = useMemo(() => {
    const items = range.data?.items ?? [];
    const weekStart = ymdParts(startOfIsoWeek(new Date()));
    let week = 0;
    let month = 0;
    for (const it of items) {
      const dateYmd = it.workday.date.slice(0, 10);
      const isToday = dateYmd === todayYmd();
      const worked = isToday ? todayWorkedMinutes : it.workday.workedMinutes;
      month += worked;
      if (dateYmd >= weekStart) week += worked;
    }
    return { weekWorkedMinutes: week, monthWorkedMinutes: month };
  }, [range.data, todayWorkedMinutes]);

  const userGreeting = useMemo(() => {
    const h = now.getHours();
    if (h < 6) return 'Buenas noches';
    if (h < 13) return 'Buenos días';
    if (h < 21) return 'Buenas tardes';
    return 'Buenas noches';
  }, [now]);

  const [pendingOvertime, setPendingOvertime] = useState<PendingOvertime | null>(null);

  // Preferencias del usuario. El toggle "Computar horas a compensar" controla
  // si al fichar OUT con balance>0 se abre el modal de aprobación (true) o si
  // las horas trabajadas de más se descartan automáticamente (false).
  const settings = useQuery({
    queryKey: ['me', 'settings'],
    queryFn: () => api<MySettings>('/me/settings'),
  });
  const overtimeCompensationEnabled = settings.data?.overtimeCompensationEnabled ?? false;
  const settingsMutation = useMutation({
    mutationFn: (next: boolean) =>
      api<MySettings>('/me/settings', {
        method: 'PATCH',
        body: JSON.stringify({ overtimeCompensationEnabled: next }),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['me', 'settings'], data);
    },
  });

  const punchMutation = useMutation({
    mutationFn: async (next: PunchType) => {
      const idempotencyKey =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return api<PunchResponse>(`/punch/${next.toLowerCase()}`, {
        method: 'POST',
        body: JSON.stringify({
          declaredLocation: location,
          projectId: next === 'IN' ? selectedProjectId : undefined,
          clientEventAt: new Date().toISOString(),
          idempotencyKey,
        }),
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['punch', 'today'] });
      queryClient.invalidateQueries({ queryKey: ['workdays', 'range'] });
      // El backend marca `pendingOvertime` cuando el OUT cierra una jornada
      // con balance positivo sin decisión previa. Abrimos el modal una vez
      // por jornada — si el usuario lo cierra, la jornada queda PENDING y
      // aparece en /solicitudes para resolverla luego.
      if (data.pendingOvertime) {
        setPendingOvertime(data.pendingOvertime);
      }
    },
  });

  const error = punchMutation.error
    ? punchMutation.error instanceof ApiError
      ? punchMutation.error.message
      : 'No se ha podido conectar con el servidor.'
    : null;

  function handlePunch() {
    if (punchMutation.isPending) return;
    punchMutation.mutate(status === 'OUT' ? 'IN' : 'OUT');
  }

  return (
    <div className="min-h-full flex flex-col">
      <header className="flex items-center justify-between gap-4 border-b border-border px-6 md:px-8 py-5">
        <div>
          <p className="font-mono text-xs font-medium uppercase tracking-cap text-muted-foreground">
            {userGreeting}
          </p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight2">{user?.fullName ?? '—'}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/ayuda#descargar-la-app"
            title="Descargar la app de escritorio o móvil"
            className="inline-flex items-center gap-1.5 rounded-full border border-line-strong bg-card px-3 py-1.5 text-xs font-medium text-ink-2 transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <Download className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Descargar app</span>
          </Link>
          {incidents.length > 0 ? <IncidentBadge incidents={incidents} /> : null}
          <HeaderStatusPill
            loading={today.isLoading}
            status={status}
            elapsedMinutes={elapsedMinutes}
          />
        </div>
      </header>

      <div className="flex-1 flex flex-col items-center justify-center gap-9 px-6 md:px-8 py-8">
        <p className="font-mono text-xs font-medium uppercase tracking-eyebrow text-muted-foreground">
          Hora actual
        </p>
        <ClockDisplay now={now} />
        <p className="text-lg text-ink-2">{capitalizeFirst(formatLongDateEs(now))}</p>

        <ProjectSelector
          status={status}
          currentProject={today.data?.currentProject ?? null}
          projects={projects.data ?? []}
          selectedProjectId={selectedProjectId}
          onChange={setSelectedProjectId}
          disabled={punchMutation.isPending}
        />

        <PunchButton
          status={status}
          loading={today.isLoading}
          pending={punchMutation.isPending}
          onClick={handlePunch}
        />

        {error ? (
          <p role="alert" className="text-sm text-destructive max-w-md text-center">
            {error}
          </p>
        ) : null}

        {showBalance ? (
          <BalanceBanner
            workedMinutes={todayWorkedMinutes}
            expectedMinutes={expectedMinutes}
            breakMinutes={breakMinutes}
            balanceMinutes={balanceMinutes}
          />
        ) : null}

        <div className="flex flex-col items-center gap-3">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span>Estoy en</span>
            <button
              type="button"
              disabled={punchMutation.isPending}
              onClick={() => setLocation((l) => (l === 'OFFICE' ? 'REMOTE' : 'OFFICE'))}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-foreground bg-card text-foreground hover:bg-foreground/[0.04] transition-colors disabled:opacity-50"
            >
              {location === 'OFFICE' ? (
                <>
                  <Building2 className="h-3.5 w-3.5" />
                  <span>Oficina</span>
                </>
              ) : (
                <>
                  <HomeIcon className="h-3.5 w-3.5" />
                  <span>Remoto</span>
                </>
              )}
            </button>
          </div>

          <OvertimeCompensationToggle
            enabled={overtimeCompensationEnabled}
            loading={settings.isLoading || settingsMutation.isPending}
            onChange={(next) => settingsMutation.mutate(next)}
          />
        </div>
      </div>

      <footer className="border-t border-border bg-paper px-6 md:px-8 py-5">
        <div className="grid grid-cols-3 gap-6 mx-auto max-w-[520px]">
          <Stat label="Hoy" value={formatDuration(todayWorkedMinutes)} />
          <Stat
            label="Esta semana"
            value={range.isLoading ? '—' : formatDuration(weekWorkedMinutes)}
          />
          <Stat
            label="Este mes"
            value={range.isLoading ? '—' : formatDuration(monthWorkedMinutes)}
          />
        </div>
      </footer>

      <OvertimeApprovalModal
        open={pendingOvertime !== null}
        workdayId={pendingOvertime?.workdayId ?? null}
        minutes={pendingOvertime?.minutes ?? 0}
        onClose={() => setPendingOvertime(null)}
      />
    </div>
  );
}

function ProjectSelector({
  status,
  currentProject,
  projects,
  selectedProjectId,
  onChange,
  disabled,
}: {
  status: 'IN' | 'OUT';
  currentProject: CurrentProject | null;
  projects: ProjectOption[];
  selectedProjectId: string | null;
  onChange: (id: string | null) => void;
  disabled: boolean;
}) {
  // Cuando estás dentro (IN): muestra pill con el proyecto activo. Read-only.
  if (status === 'IN') {
    if (!currentProject) {
      return (
        <div className="inline-flex items-center gap-2 text-[13px] text-muted-foreground">
          <Briefcase className="h-3.5 w-3.5" />
          <span>Imputando a: Sin proyecto</span>
        </div>
      );
    }
    return (
      <div className="inline-flex items-center gap-2 text-[13px] text-muted-foreground">
        <Briefcase className="h-3.5 w-3.5" />
        <span>Imputando a</span>
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-border bg-card text-foreground"
          style={{ borderColor: currentProject.color }}
        >
          <span
            className="h-2 w-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: currentProject.color }}
            aria-hidden
          />
          <span className="text-[13px] font-medium">{currentProject.name}</span>
        </span>
      </div>
    );
  }

  // OUT: desplegable para elegir el proyecto del próximo IN.
  if (projects.length === 0) return null;
  const selected = projects.find((p) => p.id === selectedProjectId) ?? null;
  return (
    <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
      <Briefcase className="h-3.5 w-3.5" />
      <span>Proyecto</span>
      <div className="relative">
        <select
          value={selectedProjectId ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
          disabled={disabled}
          aria-label="Proyecto para el próximo fichaje"
          className="appearance-none h-9 rounded-full border border-foreground bg-card text-foreground px-4 pr-8 text-[13px] font-medium focus-visible:outline-none focus-visible:border-icaria-600 transition-colors disabled:opacity-50"
          style={selected ? { borderColor: selected.color } : undefined}
        >
          <option value="">Sin proyecto</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">▾</span>
      </div>
    </div>
  );
}

/**
 * Toggle "Computar horas a compensar" en la pantalla de fichaje. Cuando está
 * activado, al fichar OUT con balance positivo aparece el modal de aprobación
 * de horas a compensar. Cuando está desactivado, las horas trabajadas de más
 * se descartan en silencio. El estado vive en `User.overtimeCompensationEnabled`
 * (vía `/me/settings`).
 */
function OvertimeCompensationToggle({
  enabled,
  loading,
  onChange,
}: {
  enabled: boolean;
  loading: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={
        enabled
          ? 'Computar horas a compensar activado — se pedirá decisión al fichar salida'
          : 'Computar horas a compensar desactivado — las horas de más no contarán al saldo'
      }
      disabled={loading}
      onClick={() => onChange(!enabled)}
      className={cn(
        'inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-sm transition-colors disabled:opacity-50',
        enabled
          ? 'border-icaria-600 bg-icaria-600/10 text-icaria-700'
          : 'border-border bg-card text-muted-foreground hover:bg-foreground/[0.04]',
      )}
    >
      <Sparkles className="h-3.5 w-3.5" />
      <span>Computar horas a compensar: {enabled ? 'Sí' : 'No'}</span>
    </button>
  );
}

function PunchButton({
  status,
  loading,
  pending,
  onClick,
}: {
  status: 'IN' | 'OUT';
  loading: boolean;
  pending: boolean;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  const disabled = loading || pending;

  // Paleta explícita: ink/foreground en reposo cuando OUT; rojo cuando IN.
  const colors = (() => {
    if (status === 'OUT') {
      return hovered
        ? { bg: '#1B23B8', border: '#1B23B8', text: '#F5F5F4' } // icaria-600 (Icaria blue)
        : { bg: '#0A0E1F', border: '#0A0E1F', text: '#F5F5F4' }; // foreground
    }
    return hovered
      ? { bg: '#A12028', border: '#A12028', text: '#FFFFFF' } // destructive 90%
      : { bg: '#B41D2A', border: '#B41D2A', text: '#FFFFFF' };
  })();

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: colors.bg,
        borderColor: colors.border,
        color: colors.text,
        transition: 'background-color 150ms, border-color 150ms, color 150ms',
      }}
      className={cn(
        'inline-flex items-center justify-center gap-2 whitespace-nowrap',
        'font-sans font-medium tracking-tightish border rounded-md',
        'disabled:pointer-events-none disabled:opacity-50',
        'active:translate-y-px',
        'w-full max-w-[520px] px-8 py-7 text-[19px]',
      )}
    >
      <Clock className="h-6 w-6" />
      {pending ? 'Registrando…' : status === 'OUT' ? 'Fichar entrada' : 'Fichar salida'}
    </button>
  );
}

function ClockDisplay({ now }: { now: Date }) {
  const text = formatTime(now);
  const [hhmm, ss] = splitClock(text);
  return (
    <p className="font-mono text-[144px] font-normal tracking-tight2 leading-none tabular-nums">
      {hhmm}
      {ss ? <span className="text-muted-foreground text-[88px]">:{ss}</span> : null}
    </p>
  );
}

function splitClock(t: string): [string, string] {
  const parts = t.split(':');
  if (parts.length >= 3) return [`${parts[0]}:${parts[1]}`, parts[2]];
  return [t, ''];
}

function formatLongDateEs(d: Date): string {
  return d.toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function capitalizeFirst(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function HeaderStatusPill({
  loading,
  status,
  elapsedMinutes,
}: {
  loading: boolean;
  status: 'IN' | 'OUT';
  elapsedMinutes: number;
}) {
  if (loading) {
    return <StatusPill state="off">Cargando…</StatusPill>;
  }
  if (status === 'IN') {
    return (
      <StatusPill state="on" duration={formatDuration(elapsedMinutes)}>
        Fichado
      </StatusPill>
    );
  }
  return <StatusPill state="off">Fuera de jornada</StatusPill>;
}

function computeTodayWorkedMinutes(punches: PunchRecord[], now: Date): number {
  let total = 0;
  let openInAt: Date | null = null;
  for (const p of punches) {
    if (p.type === 'IN') {
      openInAt = new Date(p.eventAt);
    } else if (p.type === 'OUT' && openInAt) {
      total += Math.max(0, new Date(p.eventAt).getTime() - openInAt.getTime());
      openInAt = null;
    }
  }
  if (openInAt) {
    total += Math.max(0, now.getTime() - openInAt.getTime());
  }
  return Math.floor(total / 60_000);
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="font-mono text-[10px] font-medium uppercase tracking-cap text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 font-mono text-[22px] font-medium tabular-nums">{value}</p>
    </div>
  );
}

function BalanceBanner({
  workedMinutes,
  expectedMinutes,
  breakMinutes,
  balanceMinutes,
}: {
  workedMinutes: number;
  expectedMinutes: number;
  breakMinutes: number;
  balanceMinutes: number;
}) {
  const positive = balanceMinutes >= 0;
  const netWorked = Math.max(0, workedMinutes - breakMinutes);
  return (
    <div
      className={cn(
        'inline-flex items-center gap-3 rounded-sm border px-4 py-2 text-sm font-mono tabular-nums',
        positive
          ? 'border-success/40 bg-success/10 text-success'
          : 'border-line bg-paper text-muted-foreground',
      )}
    >
      <span>
        {formatDuration(netWorked)} / {formatDuration(expectedMinutes)}
      </span>
    </div>
  );
}

const INCIDENT_LABEL: Record<WorkdayIncident, string> = {
  NO_OUT: 'Sin salida',
  OUT_WITHOUT_IN: 'Salida huérfana',
  DOUBLE_IN: 'Entrada doble',
};

function IncidentBadge({ incidents }: { incidents: WorkdayIncident[] }) {
  const label = incidents.length === 1 ? INCIDENT_LABEL[incidents[0]] : 'Incidencias';
  return (
    <Badge tone="amber" dot title={incidents.map((i) => INCIDENT_LABEL[i]).join(' · ')}>
      {label}
    </Badge>
  );
}

function ymdParts(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfIsoWeek(d: Date): Date {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  const dow = r.getDay();
  const diff = dow === 0 ? -6 : 1 - dow;
  r.setDate(r.getDate() + diff);
  return r;
}

function startOfMonth(d: Date): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), 1);
  r.setHours(0, 0, 0, 0);
  return r;
}

function todayYmd() {
  return ymdParts(new Date());
}

function monthStartYmd() {
  return ymdParts(startOfMonth(new Date()));
}

function rangeStartYmd() {
  const now = new Date();
  const wk = startOfIsoWeek(now);
  const mo = startOfMonth(now);
  return ymdParts(wk.getTime() < mo.getTime() ? wk : mo);
}
